import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import gemHistoryBg from '../assets/Images/149.jpg';
import sriLankaGemsImage from '../assets/Images/150.jpeg';
import gemIndustryImage from '../assets/Images/152.jpg';

// Animation variants (unchanged)
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
        },
    },
};

const accordionItem = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const GemHistory = () => {
    const { t } = useTranslation('gemhistory');

    const [activeIndex, setActiveIndex] = useState(null);

    const toggleService = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    // You can keep this array in component or move it to translations if you want
    // For now it's kept in code (titles & descriptions will be translated via t())
    const importanceOfGems = [
        { key: 'culturalSignificance' },
        { key: 'economicImpact' },
        { key: 'globalReputation' },
        { key: 'sustainablePractices' },
    ];

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Hero Section */}
            <div className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[50vh] lg:h-[80vh]">
                <img
                    src={gemHistoryBg}
                    alt={t('hero.alt')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#020202c7] to-[#42424259]" />

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="container relative z-10 flex items-center justify-center pt-[100px]"
                >
                    <div className="w-full">
                        <h1 className="text-center text-[50px] font-semibold leading-[80px] text-white font-copperplate md:text-[60px] lg:text-[80px]">
                            {t('hero.title')}
                        </h1>
                        <div className="flex justify-center text-center leading-8">
                            <p className="mt-6 max-w-[90%] font-lato text-[16px] font-semibold tracking-[5%] text-white sm:mt-10 sm:max-w-2xl sm:text-[25px]">
                                {t('hero.subtitle.part1')}{' '}
                                <span className="font-lato text-[16px] font-semibold tracking-[5%] text-[#BF943A] sm:text-[25px]">
                                    {t('hero.subtitle.highlight')}
                                </span>{' '}
                                {t('hero.subtitle.part2')}
                            </p>
                        </div>

                        <div className="absolute left-[30px] top-10">
                            <svg width="289" height="245" viewBox="0 0 289 245" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M289 1H1V244.5" stroke="white" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-10 right-[30px]">
                            <svg width="289" height="244" viewBox="0 0 289 244" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 243.5H288V0" stroke="white" />
                            </svg>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* History of Gems in Sri Lanka Section */}
            <section className="bg-[#1F1F1F] py-12">
                <div className="container pt-[40px] font-lato text-white md:pt-[80px]">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="flex items-center gap-5"
                    >
                        <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                        <h1 className="text-[25px] font-semibold uppercase text-white md:text-[30px] lg:text-[40px]">
                            {t('history.title')}
                        </h1>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-2 flex justify-between"
                    >
                        <div className="w-full text-[12px] font-semibold text-[#AEAEAE] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                            {t('history.subtitle')}
                        </div>
                    </motion.div>

                    <div className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></div>

                    <div className="justify-between gap-10 lg:flex">
                        <div className="relative w-full space-y-4 lg:w-[45%]">
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={scaleUp}
                                className="relative z-10 h-[330px] lg:h-[450px]"
                            >
                                <img
                                    src={sriLankaGemsImage}
                                    alt={t('history.imageAlt')}
                                    className="h-full w-full rounded-lg object-cover"
                                />
                            </motion.div>
                            {/* decorative svgs remain unchanged */}
                        </div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="relative my-10 block w-full lg:hidden lg:w-[45%]"
                        >
                            <p className="leading-8 text-[14px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                {t('history.text')}
                            </p>
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="relative hidden h-[550px] w-full lg:block lg:w-[45%]"
                        >
                            <p className="leading-8 text-[16px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                {t('history.text')}
                            </p>
                        </motion.div>
                    </div>

                    {/* About Gem Industry Section */}
                    <div className="mt-12 lg:mt-20">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="flex items-center gap-5"
                        >
                            <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                            <h1 className="text-[25px] font-semibold uppercase text-white md:text-[30px] lg:text-[40px]">
                                {t('industry.title')}
                            </h1>
                        </motion.div>

                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={textVariants}
                            className="mt-2 flex justify-between"
                        >
                            <div className="w-full text-[12px] font-semibold text-[#AEAEAE] md:text-[16px] lg:w-[60%] lg:text-[18px]">
                                {t('industry.subtitle')}
                            </div>
                        </motion.div>

                        <div className="my-6 h-px w-full bg-[#D7D7D7] lg:my-12"></div>

                        <div className="justify-between gap-10 lg:flex">
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={textVariants}
                                className="relative my-10 w-full lg:w-[45%]"
                            >
                                <p className="leading-8 text-[14px] font-semibold text-white md:text-[18px] lg:text-[20px]">
                                    {t('industry.text')}
                                </p>
                            </motion.div>

                            <div className="relative w-full space-y-4 lg:w-[45%]">
                                <motion.div
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.3 }}
                                    variants={scaleUp}
                                    className="relative z-10 h-[330px] lg:h-[450px]"
                                >
                                    <img
                                        src={gemIndustryImage}
                                        alt={t('industry.imageAlt')}
                                        className="h-full w-full rounded-lg object-cover"
                                    />
                                </motion.div>
                                {/* decorative svgs remain unchanged */}
                            </div>
                        </div>

                        {/* Importance of Gems in Sri Lanka Section */}
                        <div className="container relative py-[80px] font-redhat text-white">
                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={textVariants}
                                className="flex items-center gap-5"
                            >
                                <div className="h-px w-[40px] bg-[#D7D7D7]"></div>
                                <h1 className="text-[25px] font-semibold uppercase md:text-[30px] lg:text-[40px]">
                                    {t('importance.title')} <span className="text-[#BF943A]">{t('importance.country')}</span>
                                </h1>
                            </motion.div>

                            <motion.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={textVariants}
                                className="mt-2 w-full font-semibold text-[#AEAEAE] lg:w-[50%] lg:text-[18px]"
                            >
                                {t('importance.subtitle')}
                            </motion.div>

                            <motion.div
                                variants={staggerContainer}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, amount: 0.2 }}
                                className="mt-14 w-full space-y-4"
                            >
                                {importanceOfGems.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        variants={accordionItem}
                                        className="cursor-pointer rounded-lg border border-gray-600 bg-[#1a1a1a] p-4 transition hover:border-white"
                                        onClick={() => toggleService(index)}
                                    >
                                        <h2 className="flex items-center justify-between text-[20px] font-semibold">
                                            {t(`importance.items.${item.key}.title`)}
                                            <span className="text-[16px] lg:text-[25px]">
                                                {activeIndex === index ? "−" : "+"}
                                            </span>
                                        </h2>
                                        {activeIndex === index && (
                                            <p className="mt-2 text-[14px] lg:text-[16px]">
                                                {t(`importance.items.${item.key}.description`)}
                                            </p>
                                        )}
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default GemHistory;